import { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

// form
import { Controller, useFormContext } from 'react-hook-form';

// @mui
import { Box, IconButton, TextField } from '@mui/material';
import Iconify from '../iconify';
import TapTooltip from '../tooltip/TapTooltip';

// ----------------------------------------------------------------------

RHFTextField.propTypes = {
  name: PropTypes.string,
  reset: PropTypes.bool,
  helperText: PropTypes.node,
  formatValue: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

function buildTooltipProps(
  tooltip,
  { label, variant = 'outlined', InputLabelProps, InputProps, inputProps }
) {
  if (!tooltip) return {};

  return {
    label: (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.25em' }}>
        {label}
        <TapTooltip
          title={tooltip}
          arrow
          enterDelay={200}
          leaveDelay={200}
          disableInteractive={false}
          PopperProps={{
            modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
          }}
        >
          <IconButton size="small" sx={{ p: 0, fontSize: 'inherit' }}>
            <Iconify icon="eva:question-mark-circle-outline" width="1.25em" />
          </IconButton>
        </TapTooltip>
      </Box>
    ),
    InputLabelProps: { shrink: true, ...InputLabelProps },
    ...(variant === 'outlined' && {
      InputProps: {
        label: (
          <>
            {label}
            <Box component="span" sx={{ display: 'inline-block', width: '1.5em' }} />
          </>
        ),
        ...InputProps,
      },
    }),
    ...(typeof label === 'string' && { inputProps: { 'aria-label': label, ...inputProps } }),
  };
}

// Input numérico com foco muda de valor com a roda do mouse: quem rola a página com o cursor
// por cima dele grava outro número sem perceber.
function withBlurOnWheel(inputProps) {
  return {
    ...inputProps,
    onWheel: (event) => {
      event.currentTarget.blur();
      inputProps?.onWheel?.(event);
    },
  };
}

export default function RHFTextField({
  name,
  helperText,
  reset,
  formatValue = (value) => value,
  minLength,
  maxLength,
  tooltip,
  ...other
}) {
  const { control, setValue } = useFormContext();
  const tooltipProps = buildTooltipProps(tooltip, other);

  const inputRef = useRef();

  useEffect(() => {
    if (other?.defaultValue) {
      setValue(name, other?.defaultValue);
    }
  }, [setValue, name, other?.defaultValue]);

  useEffect(() => {
    if (other?.focus) {
      inputRef.current.focus();
    }
  }, [other?.focus]);

  useEffect(() => {
    if (reset) {
      setValue(name, '');
    }
  }, [name, reset, setValue]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref: fieldRef, ...field }, fieldState: { error } }) => (
        <TextField
          {...field}
          name={field.name}
          // O ref do Controller vai no <input>, não no `ref` do TextField: o MUI encaminha
          // `ref` para a div raiz, e o `setFocus` do react-hook-form chamaria focus() nela,
          // sem foco e sem rolar até o campo com erro.
          inputRef={(element) => {
            inputRef.current = element;
            if (typeof fieldRef === 'function') fieldRef(element);
          }}
          fullWidth
          value={typeof field.value === 'number' && field.value === 0 ? '' : field.value || ''}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
          {...tooltipProps}
          {...(other.type === 'number' && {
            inputProps: withBlurOnWheel(tooltipProps.inputProps ?? other.inputProps),
          })}
          onChange={(e) => {
            const event = {
              ...e,
              target: { ...e.target, name: field.name, value: formatValue(e.target.value) },
            };

            if (other?.onChange) {
              other.onChange(event);
            }

            field.onChange(event);
          }}
          onBlur={(event) => {
            field.onBlur();

            if (other?.onBlur) {
              other.onBlur(event);
            }
          }}
        />
      )}
    />
  );
}
